import { useTranslation } from "react-i18next";
import type { CandidateView } from "../../../api/content/curationApi";
import { useMemberNames } from "../../threads/data/useMemberNames";
import { predicateKey } from "./candidateModel";
import { iriLocalName } from "../../../shared/references/shortReferences";
import TripleList from "../page/TripleList";

/** A proposal that is closed: how it was resolved, what the commit rewrote, and the
 * statements it dropped without a replacement (v1 only counted them). */
export default function ResolvedCandidate({
  view,
  targetName,
  labels,
  projectId,
}: {
  view: CandidateView;
  targetName: string;
  labels: Readonly<Record<string, string>>;
  projectId: string;
}) {
  const { t } = useTranslation();
  const people = useMemberNames(projectId);
  const candidate = view.candidate;
  const resolution = candidate.resolution;
  const last = candidate.history[candidate.history.length - 1];
  return (
    <details className="app-card p-2 text-sm">
      <summary className="cursor-pointer">
        {resolution !== null
          ? t(`reviews.curation.resolved.${resolution.kind}`, { target: targetName, name: people.nameOf(resolution.actor) })
          : t(`reviews.curation.states.${candidate.state}`, { target: targetName })}
        {last !== undefined && last.reason !== "" ? <span className="app-text-muted"> · {last.reason}</span> : null}
      </summary>
      {resolution !== null ? (
        <div className="mt-2 space-y-2">
          {resolution.items.length > 0 ? (
            <ul className="list-disc pl-5 text-xs">
              {resolution.items.map((item) => (
                <li key={item.itemId}>
                  {t("reviews.curation.resolved.item", {
                    name: item.name,
                    uses: t("reviews.curation.resolved.uses", { count: item.rewrittenUses }),
                    added: t("data.counts.statements", { count: item.addedTriples }),
                    removed: item.removedTriples,
                  })}
                </li>
              ))}
            </ul>
          ) : (
            <p className="app-text-muted text-xs">{t("reviews.curation.resolved.noCommit")}</p>
          )}
          {resolution.fieldOutcomes.length > 0 ? (
            <p className="text-xs">
              {resolution.fieldOutcomes
                .map((choice) => {
                  const key = predicateKey(choice.predicate);
                  return `${key !== null ? t(`reviews.curation.fields.${key}`) : iriLocalName(choice.predicate)}: ${t(`reviews.curation.actions.${choice.action}`)}`;
                })
                .join(" · ")}
            </p>
          ) : null}
          {resolution.droppedTripleCount > 0 ? (
            <TripleList title={t("reviews.curation.resolved.dropped", { count: resolution.droppedTripleCount })} lines={resolution.droppedTriples} labels={labels} tone="removed" />
          ) : null}
        </div>
      ) : null}
    </details>
  );
}
